/**
 * Shared SSE (Server-Sent Events) parsing utilities for streaming AI responses.
 * 
 * Handles:
 * - Partial JSON chunks split across frames
 * - CRLF and LF line endings
 * - Multiple events in one chunk
 * - Trailing data without final newline
 * - Keepalive/comment lines (lines starting with ':')
 * - Role-only or tool-call deltas (filtered out - only non-empty text content returned)
 */

export interface SSEParseResult {
  /** Extracted text content, or null if this event has no meaningful text */
  text: string | null;
  /** Optional conversation ID passed through from some endpoints */
  conversationId?: string;
  /** True when the stream signals completion ([DONE] or {done:true}) */
  done: boolean;
}

/**
 * Parse a single SSE JSON payload string and extract text content.
 * Supports multiple response shapes from different AI providers:
 *   - OpenAI-style: choices[0].delta.content
 *   - Simple: .text, .delta.text, .content
 *   - Done signals: "[DONE]" string or {done: true}
 *
 * Returns null text for role-only deltas, tool_calls, empty content, or whitespace-only content.
 */
export function extractSSEContent(jsonStr: string): SSEParseResult {
  if (jsonStr === '[DONE]') return { text: null, done: true };

  try {
    const parsed = JSON.parse(jsonStr);
    if (parsed.done === true) return { text: null, done: true };

    // Extract text from various payload shapes
    const raw: unknown =
      parsed.choices?.[0]?.delta?.content ??
      parsed.text ??
      parsed.delta?.text ??
      parsed.content ??
      null;

    // Filter out non-string, empty, or whitespace-only content
    const text = typeof raw === 'string' && raw.length > 0 ? raw : null;

    return {
      text,
      conversationId: parsed.conversationId,
      done: false,
    };
  } catch {
    // Malformed JSON - safe to skip (partial chunk will be retried on next frame)
    return { text: null, done: false };
  }
}

export interface SSELineBuffer {
  /** Internal buffer - do not access directly */
  _buffer: string;
}

/**
 * Create a new SSE line buffer for accumulating partial chunks.
 */
export function createSSELineBuffer(): SSELineBuffer {
  return { _buffer: '' };
}

/**
 * Feed raw bytes (decoded text) into the buffer and yield complete SSE lines.
 * Handles CRLF, LF, and partial lines that span multiple chunks.
 * 
 * After the stream reader signals done, call flushSSELineBuffer() to handle
 * any trailing data without a final newline.
 */
export function feedSSELineBuffer(buf: SSELineBuffer, chunk: string): string[] {
  buf._buffer += chunk;
  const lines: string[] = [];
  let newlineIndex: number;

  while ((newlineIndex = buf._buffer.indexOf('\n')) !== -1) {
    let line = buf._buffer.slice(0, newlineIndex);
    buf._buffer = buf._buffer.slice(newlineIndex + 1);

    // Strip trailing \r for CRLF line endings
    if (line.endsWith('\r')) line = line.slice(0, -1);

    lines.push(line);
  }

  return lines;
}

/**
 * Flush any remaining data in the buffer as a final line.
 * Call this after the stream reader signals done.
 */
export function flushSSELineBuffer(buf: SSELineBuffer): string | null {
  const remaining = buf._buffer.trim();
  buf._buffer = '';
  return remaining.length > 0 ? remaining : null;
}

/**
 * Process a single SSE line and extract content if it's a data event.
 * Skips empty lines, comment lines (starting with ':'), and non-data lines.
 * 
 * Returns the parse result, or null if the line should be skipped.
 */
export function processSSELine(line: string): SSEParseResult | null {
  // Skip empty lines, comment/keepalive lines, and non-data lines
  if (line.trim() === '' || line.startsWith(':') || !line.startsWith('data: ')) {
    return null;
  }

  const payload = line.slice(6).trim();
  if (payload.length === 0) return null;

  return extractSSEContent(payload);
}

/**
 * High-level helper: process a decoded chunk through the buffer and return
 * all text content and control signals found.
 * 
 * Usage:
 * ```ts
 * const buf = createSSELineBuffer();
 * // In streaming loop:
 * const { texts, done } = processSSEChunk(buf, decodedChunk);
 * assistantText += texts.join('');
 * if (done) break;
 * 
 * // After reader.read() returns done:
 * const { texts: finalTexts, done: finalDone } = flushAndProcessSSE(buf);
 * assistantText += finalTexts.join('');
 * ```
 */
export function processSSEChunk(
  buf: SSELineBuffer,
  chunk: string
): { texts: string[]; done: boolean; conversationId?: string } {
  const lines = feedSSELineBuffer(buf, chunk);
  const texts: string[] = [];
  let done = false;
  let conversationId: string | undefined;

  for (const line of lines) {
    const result = processSSELine(line);
    if (!result) continue;
    if (result.done) { done = true; break; }
    if (result.text) texts.push(result.text);
    if (result.conversationId) conversationId = result.conversationId;
  }

  return { texts, done, conversationId };
}

/**
 * Flush the buffer and process any remaining trailing line.
 */
export function flushAndProcessSSE(
  buf: SSELineBuffer
): { texts: string[]; done: boolean; conversationId?: string } {
  const remaining = flushSSELineBuffer(buf);
  if (!remaining) return { texts: [], done: false };

  const result = processSSELine(remaining);
  if (!result) return { texts: [], done: false };
  if (result.done) return { texts: [], done: true };
  return {
    texts: result.text ? [result.text] : [],
    done: false,
    conversationId: result.conversationId,
  };
}
